/**
 * A choice as a card — one of a `RadioGroup`'s options, the whole card the
 * target (its label covers it), the radio at its start. Chosen, it wears the
 * tone's edge and tint; a choice that can be taken shows the tone's line on
 * hover. A keyboard focus rings the card, not only the dot.
 *
 * Presentation only: the card's words are the caller's, translated.
 */
import type { Tone } from '@tonnie/design-tokens';
import type { ReactNode } from 'react';

import { TONE_CLASSES } from '../lib/tone-classes';
import { cn } from '../lib/utils';
import { Label } from '../primitives/label';
import { RadioGroupItem } from '../primitives/radio-group';

export interface RadioCardProps {
  id: string;
  value: string;
  selected: boolean;
  children: ReactNode;
  tone?: Tone;
  className?: string;
  'data-testid'?: string;
}

export function RadioCard({
  id,
  value,
  selected,
  children,
  tone,
  className,
  'data-testid': testId,
}: RadioCardProps) {
  const look = tone
    ? TONE_CLASSES[tone]
    : { lineHover: 'hover:border-primary/40', edge: 'border-primary', subtle: 'bg-primary/5' };
  return (
    <div
      data-testid={testId}
      data-selected={selected}
      data-slot="radio-card"
      className={cn(
        'relative flex items-start gap-3 rounded-xl border p-4 transition-colors',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
        look.lineHover,
        selected ? cn(look.edge, look.subtle) : 'border-border bg-card',
        className
      )}
    >
      <RadioGroupItem value={value} id={id} className="mt-0.5" />
      <Label
        htmlFor={id}
        className="block min-w-0 flex-1 cursor-pointer font-normal leading-normal after:absolute after:inset-0 after:rounded-xl after:content-['']"
      >
        {children}
      </Label>
    </div>
  );
}
